import React, { useState, useEffect } from 'react';
import { isPortalDark, onPortalThemeChange } from './utils/portalTheme';
import axios from 'axios';
import io from 'socket.io-client';
import './App.css';
import BotStatus from './components/BotStatus';
import MetricsOverview from './components/MetricsOverview';
import MessageChart from './components/MessageChart';
import UsersList from './components/UsersList';
import NotificationHistory from './components/NotificationHistory';
import SystemStats from './components/SystemStats';
import ThemeToggle from './components/ThemeToggle';
import UserTickerEditor from './components/UserTickerEditor';
import LinkTelegram from './components/LinkTelegram';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || window.location.origin;

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [metrics, setMetrics] = useState({
    messages_sent: 0,
    messages_failed: 0,
    messages_per_second: 0,
    throttle_events: 0,
    active_users: 0,
    queue_sizes: {},
    rate_limit_stats: {},
    system_stats: {},
    ticker_stats: {},
    user_stats: {},
    bot_status: 'stopped',
    uptime: 0,
    notification_history: []
  });
  
  const [users, setUsers] = useState([]);
  // Theme is chosen once in the portal and published as the portal_theme
  // cookie; this dashboard follows it rather than keeping its own preference.
  const [isDarkMode, setIsDarkMode] = useState(() => isPortalDark());
  const [botActionLoading, setBotActionLoading] = useState(null); // null | 'start' | 'stop' | 'restart'
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [linkInfo, setLinkInfo] = useState({ is_admin: false, linked: false });
  const [portalUsers, setPortalUsers] = useState([]);

  useEffect(() => {
    // Check authentication first
    checkAuthentication();
  }, []);

  useEffect(() => {
    // Apply theme
    document.body.className = isDarkMode ? 'dark-theme' : 'light-theme';
  }, [isDarkMode]);

  // The portal owns the theme; re-read its cookie when this tab regains focus.
  useEffect(() => onPortalThemeChange((next) => setIsDarkMode(next === 'dark')), []);

  const checkAuthentication = async () => {
    try {
      // Get URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const userParam = urlParams.get('user');
      const tokenParam = urlParams.get('token');

      // Include user and token parameters if present
      const params = new URLSearchParams();
      if (userParam) params.set('user', userParam);
      if (tokenParam) params.set('token', tokenParam);
      const authUrl = params.toString() ? `${API_URL}/auth/check?${params.toString()}` : `${API_URL}/auth/check`;

      const response = await axios.get(authUrl, { withCredentials: true });
      
      if (response.data.authenticated) {
        setIsAuthenticated(true);
        setAuthChecking(false);
        // Clean token from URL after successful auth
        try {
          if (tokenParam) {
            const cleanParams = new URLSearchParams(window.location.search);
            cleanParams.delete('token');
            const cleanUrl = cleanParams.toString()
              ? `${window.location.pathname}?${cleanParams.toString()}`
              : window.location.pathname;
            window.history.replaceState({}, '', cleanUrl);
          }
        } catch (e) { /* ignore replaceState errors */ }
      } else {
        // Redirect to portal login
        window.location.href = response.data.redirect || 'https://visual.eigentrage.com/login';
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // Redirect to portal login on error
      window.location.href = 'https://visual.eigentrage.com/login';
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    
    // Initialize socket connection
    const socketInstance = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      withCredentials: true
    });

    socketInstance.on('connect', () => {
      console.log('Connected to server');
    });

    socketInstance.on('connect_error', (error) => {
      console.error('Socket connection error:', error.message);
    });

    socketInstance.on('metrics_update', (data) => {
      setMetrics(data);
    });

    // Fetch initial data
    fetchMetrics();
    fetchUsers();
    fetchLinkInfo();

    // Set up polling
    const interval = setInterval(() => {
      fetchMetrics();
      fetchUsers();
    }, 5000);

    return () => {
      clearInterval(interval);
      if (socketInstance) {
        socketInstance.disconnect();
      }
    };
  }, [isAuthenticated]);

  const getApiUrl = (endpoint) => {
    const urlParams = new URLSearchParams(window.location.search);
    const userParam = urlParams.get('user');
    const separator = endpoint.includes('?') ? '&' : '?';
    return userParam ? `${API_URL}${endpoint}${separator}user=${userParam}` : `${API_URL}${endpoint}`;
  };

  const fetchMetrics = async () => {
    try {
      const response = await axios.get(getApiUrl('/metrics'), { withCredentials: true });
      console.log('Metrics response:', response.data);
      console.log('Bot status:', response.data.bot_status);
      setMetrics(response.data);
    } catch (error) {
      console.error('Error fetching metrics:', error);
      if (error.response?.status === 401) {
        window.location.href = error.response.data.redirect || 'https://visual.eigentrage.com/login';
      }
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(getApiUrl('/users'), { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      if (error.response?.status === 401) {
        window.location.href = error.response.data.redirect || 'https://visual.eigentrage.com/login';
      }
    }
  };

  // Who the caller is and what they may see. Drives whether the admin linking
  // controls appear; the backend enforces the same rules regardless.
  const fetchLinkInfo = async () => {
    try {
      const res = await axios.get(getApiUrl('/link/status'), { withCredentials: true });
      setLinkInfo(res.data);
      if (res.data.is_admin) {
        try {
          const pu = await axios.get(getApiUrl('/portal-users'), { withCredentials: true });
          setPortalUsers(pu.data);
        } catch (e) { /* non-fatal: linking dropdown just stays empty */ }
      }
    } catch (error) {
      console.error('Error fetching link status:', error);
    }
  };

  const handleLinkUser = async (userId, portalUsername) => {
    try {
      await axios.put(
        getApiUrl(`/users/${userId}/link`),
        { portal_username: portalUsername },
        { withCredentials: true }
      );
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.error || 'Could not update the link');
    }
  };

  const handleBotAction = async (action) => {
    setBotActionLoading(action);
    try {
      await axios.post(getApiUrl(`/bot/${action}`), {}, { withCredentials: true });
      // Poll for status change
      let attempts = 0;
      const expectedStatus = action === 'stop' ? 'stopped' : 'running';
      const pollStatus = setInterval(async () => {
        try {
          const response = await axios.get(getApiUrl('/metrics'), { withCredentials: true });
          const currentStatus = response.data.bot_status;
          attempts++;
          
          // Check if status has changed to expected state
          if (currentStatus === expectedStatus || attempts > 10) {
            clearInterval(pollStatus);
            setBotActionLoading(null);
            // Update metrics with fresh data
            setMetrics(response.data);
          }
        } catch (err) {
          console.error('Error polling status:', err);
          if (attempts > 10) {
            clearInterval(pollStatus);
            setBotActionLoading(null);
          }
        }
      }, 1000);
    } catch (error) {
      console.error(`Error ${action}ing bot:`, error);
      alert(`Failed to ${action} bot: ${error.response?.data?.error || error.message}`);
      setBotActionLoading(null);
    }
  };

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  // Derived rather than stored: `users` is refetched every 5s, and holding a
  // copy of the selected user would go stale against the list.
  const selectedUser = users.find((u) => u.user_id === selectedUserId) || null;

  if (authChecking) {
    return (
      <div className="App" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <h2>Authenticating...</h2>
          <p>Please wait while we verify your credentials.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <h1>Telegram Signal Bot Dashboard</h1>
        <ThemeToggle isDarkMode={isDarkMode} onToggle={toggleTheme} />
      </header>
      
      <div className="dashboard-container">
        <div className="dashboard-grid">
          <BotStatus 
            status={metrics.bot_status} 
            uptime={metrics.uptime}
            onStart={() => handleBotAction('start')}
            onStop={() => handleBotAction('stop')}
            onRestart={() => handleBotAction('restart')}
            loadingAction={botActionLoading}
          />
          
          <NotificationHistory history={metrics.notification_history} />
          
          <MetricsOverview metrics={metrics} />
          
          <MessageChart metrics={metrics} />
          
          <SystemStats stats={metrics.system_stats} />
          
          <LinkTelegram getApiUrl={getApiUrl} onLinked={fetchUsers} />

          <UsersList
            users={users}
            userStats={metrics.user_stats}
            selectedUserId={selectedUserId}
            onSelectUser={setSelectedUserId}
            isAdmin={linkInfo.is_admin}
            portalUsers={portalUsers}
            onLinkUser={handleLinkUser}
          />

          {selectedUser && (
            <UserTickerEditor
              user={selectedUser}
              getApiUrl={getApiUrl}
              onSaved={fetchUsers}
              onClose={() => setSelectedUserId(null)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default App;